// 📁 src/pages/Privacy.js
import React from "react";
import Seo from "../components/seo";
import { currentLanguage } from "../utils/locale";

/* =========================================================
   THEME TOKENS (match About/Disclaimer exactly)
========================================================= */
const WRAP = "px-[1vw]";                // global side padding
const MAXW = "max-w-10xl mx-auto";      // page max width
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " + BORDER_SOFT + " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background (identical) */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Panels (non-glass, textile-like) */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO helpers (About.js-style)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/privacy`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;   // ✅ match About.js
const logoWebp = `${siteUrl}/icons/logo.webp`;

const metaDescription =
  "Your privacy matters. TarotFate minimizes data collection, uses no tracking for readings and relies on trusted third parties like PayPal and Google Ads under their own policies.";

const keywords = `
privacy, privacy policy, no tracking, no cookies for readings, tarot privacy, data protection,
personal data, GDPR friendly, CCPA friendly, PayPal donations, Google Ads policy, cookie usage,
TarotFate privacy, anonymous tarot, reflective tarot, spiritual insight privacy
`
  .replace(/\s+/g, " ")
  .trim();

const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: logoWebp,
        width: 512,
        height: 512
      }
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: "en"
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Privacy Policy | TarotFate",
      description: metaDescription,
      isPartOf: { "@id": `${siteUrl}#website` },
      primaryImageOfPage: { "@id": `${pageUrl}#image` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
      inLanguage: "en"
    },
    {
      "@type": "ImageObject",
      "@id": `${pageUrl}#image`,
      url: ogImage,
      width: 1200,
      height: 630,
      caption: "TarotFate cover"
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Privacy", item: pageUrl }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Does TarotFate collect personal data?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No personal data is required to use tarot readings. Interactions are intended to remain within your session and are not used to create user profiles."
          }
        },
        {
          "@type": "Question",
          name: "Does TarotFate use cookies or trackers?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "The core reading experience does not rely on tracking cookies. Advertising via Google Ads may use its own cookies per Google's policies."
          }
        },
        {
          "@type": "Question",
          name: "How are donations handled?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Donations are processed securely by PayPal. TarotFate does not collect or store payment card details."
          }
        },
        {
          "@type": "Question",
          name: "Do you share or sell user data?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No. TarotFate does not sell, rent or trade personal data."
          }
        }
      ]
    }
  ]
};

export default function Privacy() {
  const isSpanish = currentLanguage() === "es";
  const copy = isSpanish
    ? {
        title: "Privacidad",
        seoTitle: "Politica de privacidad | TarotFate",
        description:
          "TarotFate minimiza la recopilacion de datos, no requiere informacion personal para las lecturas y usa servicios externos como PayPal y Google Ads bajo sus propias politicas.",
        intro:
          "TarotFate.com mantiene la privacidad simple: no necesitas datos personales para las lecturas, las donaciones pasan por PayPal y los anuncios siguen las politicas de Google.",
        paragraphs: [
          "Tu privacidad importa. Este sitio no requiere datos personales para usar las lecturas de tarot. Las interacciones estan pensadas para permanecer dentro de tu sesion y no se usan para crear perfiles de usuario.",
          "TarotFate.com no usa cookies para rastrear tus lecturas ni crear perfiles basados en tu uso del sitio. Cualquier interaccion con la experiencia principal de lectura permanece privada dentro de tu sesion.",
          "Los servicios externos pueden incluir PayPal para donaciones y Google Ads para publicidad. PayPal procesa los pagos de forma segura y TarotFate no recopila ni almacena datos financieros. Google puede usar cookies o tecnologias similares segun sus propias politicas.",
          "Este sitio no vende, alquila ni intercambia datos personales.",
          "Por respeto a tu privacidad y bienestar emocional, evita compartir detalles altamente sensibles o identificables en tus preguntas. El tarot es para reflexion, no para revelar crisis personales.",
          "Al usar este sitio, aceptas este enfoque simple y honesto de privacidad. Para el uso previsto del sitio, consulta el aviso legal.",
        ],
        faqTitle: "Preguntas frecuentes",
        faq: [
          {
            q: "TarotFate recopila datos personales?",
            a: "No se requieren datos personales para usar las lecturas de tarot. Las interacciones no se usan para crear perfiles de usuario.",
          },
          {
            q: "TarotFate usa cookies o rastreadores?",
            a: "La experiencia principal de lectura no depende de cookies de seguimiento. La publicidad de Google Ads puede usar sus propias cookies segun sus politicas.",
          },
          {
            q: "Como se manejan las donaciones?",
            a: "Las donaciones son procesadas de forma segura por PayPal. TarotFate no recopila ni almacena datos de tarjetas de pago.",
          },
          {
            q: "Comparten o venden datos de usuarios?",
            a: "No. TarotFate no vende, alquila ni intercambia datos personales.",
          },
        ],
      }
    : {
        title: "Privacy",
        seoTitle: "Privacy Policy | TarotFate",
        description: metaDescription,
        intro:
          "TarotFate.com keeps it simple: no personal data required for readings, donations via PayPal and ads under Google's own policies.",
        paragraphs: [
          "Your privacy matters deeply. This website does not collect, store or track any personally identifiable information. All tarot readings are based solely on the input you provide, anonymously and without being saved.",
          "TarotFate.com does not use cookies to track your behavior, nor does it create profiles based on your usage. Any interaction you have with this site remains private and confined to your session.",
          "The only third-party services involved may include PayPal for donations and Google Ads for advertising. PayPal handles payment processing securely, and Google may use cookies or similar technologies under its own policies.",
          "This site does not share, sell or disclose your information, because it does not collect it to begin with.",
          "Out of respect for your privacy and emotional well-being, please avoid sharing highly sensitive or identifying details in your whispers. Tarot is for reflection, not crisis disclosure.",
          "By using this site, you agree to this simple, honest approach to privacy. For intended use of the site, see the Disclaimer.",
        ],
        faqTitle: "Frequently Asked Questions",
        faq: [
          {
            q: "Does TarotFate collect personal data?",
            a: "No personal data is required to use tarot readings. Interactions are intended to remain within your session and are not used to create user profiles.",
          },
          {
            q: "Does TarotFate use cookies or trackers?",
            a: "The core reading experience does not rely on tracking cookies. Advertising via Google Ads may use its own cookies per Google's policies.",
          },
          {
            q: "How are donations handled?",
            a: "Donations are processed securely by PayPal. TarotFate does not collect or store payment card details.",
          },
          {
            q: "Do you share or sell user data?",
            a: "No. TarotFate does not sell, rent or trade personal data.",
          },
        ],
      };

  return (
    <div
      className={`${FONT} text-white min-h-screen relative overflow-hidden`}
      style={{ background: CANVAS_BG }}
    >
      {/* Centralized SEO (consistent with About.js) */}
      <Seo
        title={copy.seoTitle}
        description={copy.description}
        url={isSpanish ? `${siteUrl}/es/privacy` : pageUrl}
        image={ogImage}
        schema={isSpanish ? undefined : schemaGraph}
        keywords={keywords}
        includeMega={true}
      />

      {/* Ambient auras (no dots/noise) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* =========================
          HERO
      ========================= */}
      <section className={`pt-6 ${WRAP} ${MAXW} text-center`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">
          {copy.title}
        </h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100 drop-shadow-[0_0_0px_rgba(203,183,234,0.8)]">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>
          {copy.intro}
        </p>
      </section>

      {/* =========================
          PRIVACY PANEL (silk, non-glass)
      ========================= */}
      <section className={`mt-8 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-2 sm:p-6 md:p-6`}>
          <div className={`relative z-[1] ${TEXT} text-[17px] md:text-[18px] leading-[1.8]`}>
            {copy.paragraphs.map((paragraph, index) => (
              <p key={paragraph} className={index ? "mt-5" : ""}>{paragraph}</p>
            ))}
          </div>
          {/* top edge gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* =========================
          FAQ - compact, left text + right "+"
      ========================= */}
      <section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
        <div className="text-center">
          <h2 className="text-base sm:text-lg font-medium">{copy.faqTitle}</h2>
        </div>

        <div className="mt-6 rounded-sm border border-white/35 bg-white/5 divide-y divide-white/25">
          {copy.faq.map(({ q, a }, i) => (
            <details key={i} className="group">
              <summary
                className="
                  cursor-pointer list-none px-4 md:px-6 py-3
                  text-sm sm:text-sm text-white/95 font-medium
                  flex items-start justify-between gap-6
                  after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none
                  group-open:after:content-['-']
                "
              >
                <span className="pr-2">{q}</span>
              </summary>
              <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>
                {a}
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
